import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Loader2, Lock, Share2, Sparkles, Zap } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { marketApi, STICKER_PACK_PRICE } from "../lib/market";
import { shareOrCopy } from "../lib/share";
import { formatApiError } from "../lib/api";
import { NexaTokensChip } from "../components/NexaTokensChip";

/* -------------------- Helpers -------------------- */

function _formatDate(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "";
    return d.toLocaleDateString("es-MX", { day: "2-digit", month: "short", year: "numeric" });
  } catch (_e) {
    return "";
  }
}

/* -------------------- Insufficient balance modal -------------------- */

function InsufficientBalanceModal({ open, missing, ondatag, onClose }) {
  useEffect(() => {
    if (!open) return undefined;
    const original = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e) => {
      if (e.key === "Escape") onClose?.();
    };
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = original;
      window.removeEventListener("keydown", onKey);
    };
  }, [open, onClose]);

  const share = useCallback(async () => {
    const origin = typeof window !== "undefined" ? window.location.origin : "";
    await shareOrCopy({
      title: "Qué Onda Chat",
      text: `¡Únete a Qué Onda Chat y ganamos NexaTokens juntos! Encuéntrame como @${ondatag || "amig@"}`,
      url: origin,
      ref: ondatag || undefined,
    });
    // Do not auto-close — let the user see the toast; they can close manually.
  }, [ondatag]);

  if (!open) return null;
  return (
    <div
      className="fixed inset-0 z-[95] flex items-center justify-center px-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="market-insufficient-title"
      data-testid="market-insufficient-modal"
    >
      <button
        type="button"
        aria-label="Cerrar"
        onClick={onClose}
        data-testid="market-insufficient-overlay"
        className="absolute inset-0 bg-black/65 backdrop-blur-sm border-0 p-0 cursor-pointer"
      />
      <div
        className="relative q-glass rounded-3xl border border-white/10 shadow-2xl w-full max-w-md p-6 sm:p-7"
        style={{ boxShadow: "0 0 60px rgba(0, 240, 255, 0.18)" }}
      >
        <div className="flex items-center gap-3 mb-3">
          <div
            className="h-10 w-10 rounded-2xl flex items-center justify-center"
            style={{
              background: "rgba(0, 240, 255, 0.12)",
              border: "1px solid rgba(0, 240, 255, 0.42)",
              boxShadow: "0 0 24px rgba(0, 240, 255, 0.3)",
            }}
          >
            <Sparkles size={20} className="q-accent-text" />
          </div>
          <h3
            id="market-insufficient-title"
            className="font-display text-xl font-bold tracking-tight"
          >
            Te faltan <span className="q-accent-text">{missing}</span> NexaTokens
          </h3>
        </div>
        <p className="text-sm text-slate-300 leading-relaxed mb-5">
          ¡Invita a 5 amigos y desbloquéalo gratis! Cada amigo que se registre con
          tu enlace te da <span className="font-mono q-accent-text">+5 NexaTokens</span>.
        </p>
        <button
          type="button"
          onClick={share}
          className="q-btn-primary w-full inline-flex items-center justify-center gap-2 !py-3"
          data-testid="market-insufficient-share"
        >
          <Share2 size={16} /> Compartir mi enlace
        </button>
        <button
          type="button"
          onClick={onClose}
          className="w-full mt-2 py-2 text-xs uppercase tracking-widest font-mono text-slate-400 hover:text-slate-200"
          data-testid="market-insufficient-close"
        >
          Ahora no
        </button>
      </div>
    </div>
  );
}

/* -------------------- Pack card -------------------- */

function PackCard({ pack, busy, onPurchase }) {
  const unlocked = !!pack.unlocked;
  return (
    <article
      className="pack-card relative rounded-3xl overflow-hidden q-glass border border-white/10 flex flex-col"
      data-testid={`market-pack-${pack.id}`}
      data-unlocked={unlocked ? "true" : "false"}
      style={{
        // Per-pack accent used by the shine + border glow
        "--pack-accent": pack.accent || "#06B6D4",
        boxShadow: unlocked
          ? "0 0 28px rgba(0, 240, 255, 0.35), inset 0 0 0 1px rgba(0, 240, 255, 0.35)"
          : "0 8px 24px rgba(0, 0, 0, 0.35)",
      }}
    >
      <div className="relative aspect-[4/3] overflow-hidden">
        <img
          src={pack.cover_url}
          alt={`Portada del pack ${pack.name}`}
          loading="lazy"
          className="w-full h-full object-cover pack-cover"
          data-testid={`market-pack-${pack.id}-cover`}
        />
        {/* Holographic shine sweep on hover/tap */}
        <div className="pack-shine" aria-hidden="true" />
        <div
          className="absolute inset-0 pointer-events-none"
          aria-hidden="true"
          style={{
            background:
              "linear-gradient(180deg, transparent 45%, rgba(4,10,20,0.85) 100%)",
          }}
        />
        <div className="absolute top-3 right-3">
          <span
            className="inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[11px] font-mono font-semibold"
            style={{
              color: "#00f0ff",
              background: "rgba(3, 10, 20, 0.72)",
              border: "1px solid rgba(0, 240, 255, 0.45)",
              boxShadow: "0 0 14px rgba(0, 240, 255, 0.35)",
            }}
            data-testid={`market-pack-${pack.id}-price`}
          >
            <Zap size={12} strokeWidth={2.6} />
            {pack.price} NexaTokens
          </span>
        </div>
        {unlocked && (
          <div className="absolute bottom-3 left-3">
            <span
              className="inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[10px] font-mono font-bold uppercase tracking-widest"
              style={{
                color: "#001018",
                background: "rgba(0, 240, 255, 0.92)",
                border: "1px solid rgba(0, 240, 255, 0.9)",
                boxShadow: "0 0 20px rgba(0, 240, 255, 0.6)",
              }}
              data-testid={`market-pack-${pack.id}-unlocked-badge`}
            >
              ✓ Desbloqueado
            </span>
          </div>
        )}
      </div>

      <div className="p-4 flex-1 flex flex-col">
        <h2
          className="font-display text-lg font-bold tracking-tight mb-1"
          data-testid={`market-pack-${pack.id}-name`}
        >
          {pack.name}
        </h2>
        <p className="text-sm text-slate-300 leading-snug mb-4 min-h-[2.5rem]">
          {pack.description}
        </p>

        {unlocked ? (
          <>
            <div className="grid grid-cols-4 gap-1.5 mb-3" aria-hidden="true">
              {Array.from({ length: 4 }).map((_, i) => (
                <div
                  key={i}
                  className="aspect-square rounded-xl overflow-hidden border border-white/10 pack-sticker-tile"
                  style={{
                    // subtle per-tile hue shift for the "collection" feel
                    filter: `hue-rotate(${i * 22}deg) saturate(1.15)`,
                  }}
                >
                  <img
                    src={pack.cover_url}
                    alt=""
                    aria-hidden="true"
                    className="w-full h-full object-cover"
                  />
                </div>
              ))}
            </div>
            <div
              className="text-[10px] text-slate-400 uppercase tracking-widest font-mono mt-auto"
              data-testid={`market-pack-${pack.id}-purchased-at`}
            >
              Comprado {_formatDate(pack.purchased_at)}
            </div>
            <div
              className="text-[10px] text-slate-500 mt-1 italic"
              data-testid={`market-pack-${pack.id}-soon`}
            >
              Próximamente: úsalos en el chat.
            </div>
          </>
        ) : (
          <button
            type="button"
            onClick={() => onPurchase(pack)}
            disabled={busy}
            className="mt-auto q-btn-primary w-full inline-flex items-center justify-center gap-2 !py-3 disabled:opacity-60"
            data-testid={`market-pack-${pack.id}-buy`}
          >
            {busy ? (
              <Loader2 size={16} className="animate-spin" />
            ) : (
              <Lock size={16} />
            )}
            Desbloquear · {pack.price}
            <Zap size={14} strokeWidth={2.6} />
          </button>
        )}
      </div>
    </article>
  );
}

/* -------------------- Main page -------------------- */

export default function MarketPage() {
  const { user, refreshUser } = useAuth();
  const [packs, setPacks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [purchasingId, setPurchasingId] = useState(null);
  const [insufficient, setInsufficient] = useState(null); // { missing } | null

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await marketApi.list();
      setPacks(data);
    } catch (_e) {
      toast.error("No pude cargar los packs. Intenta de nuevo.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const balance = user?.nexatokens_balance || 0;
  const unlockedCount = useMemo(() => packs.filter((p) => p.unlocked).length, [packs]);

  const onPurchase = useCallback(
    async (pack) => {
      if (purchasingId) return;
      if (balance < pack.price) {
        setInsufficient({ missing: pack.price - balance });
        return;
      }
      setPurchasingId(pack.id);
      try {
        const res = await marketApi.purchase(pack.id);
        toast.success(`¡Desbloqueaste ${pack.name}! ✨`);
        try {
          // Optimistic global event → chip pulses + AuthContext balance refresh
          window.dispatchEvent(
            new CustomEvent("queonda.tokens.update", {
              detail: {
                type: "tokens.update",
                balance: res.balance,
                delta: -pack.price,
                reason: "sticker_purchase",
              },
            })
          );
        } catch (_e) {
          /* ignore */
        }
        setPacks((prev) =>
          prev.map((p) =>
            p.id === pack.id ? { ...p, unlocked: true, purchased_at: res.purchased_at } : p
          )
        );
        // Server is source of truth for balance — refresh the user profile too.
        refreshUser?.();
      } catch (err) {
        const status = err?.response?.status;
        const detail = err?.response?.data?.detail;
        if (status === 402 && detail && typeof detail === "object") {
          const missing = detail.needed ? detail.needed - (detail.balance || 0) : pack.price - balance;
          setInsufficient({ missing: Math.max(1, missing) });
        } else if (status === 409) {
          toast.info("Ya tienes este pack.");
          load();
        } else {
          toast.error(formatApiError(err));
        }
      } finally {
        setPurchasingId(null);
      }
    },
    [balance, purchasingId, refreshUser, load]
  );

  return (
    <main
      className="min-h-screen pt-24 pb-32 px-4 sm:px-6"
      data-testid="market-page"
    >
      <div className="max-w-3xl mx-auto">
        <header className="mb-6 relative">
          <span className="q-chip">
            <span
              className="h-1.5 w-1.5 rounded-full q-accent-bg"
              style={{ boxShadow: "0 0 8px var(--accent-glow)" }}
            />
            Tienda Virtual
          </span>
          <h1
            className="font-display text-3xl sm:text-4xl font-bold tracking-tight mt-3"
            data-testid="market-title"
          >
            HoloStickers <span className="q-accent-text">3D</span>
          </h1>
          <p className="text-sm text-slate-400 mt-2 max-w-lg">
            Canjea tus <span className="q-accent-text font-mono">NexaTokens</span> por
            packs holográficos exclusivos. Cada pack se desbloquea una vez.
          </p>
          <div className="flex items-center gap-2 mt-4" data-testid="market-header-status">
            <NexaTokensChip balance={balance} size="lg" />
            <span
              className="text-[10px] uppercase font-mono tracking-widest text-slate-500 ml-1"
              data-testid="market-unlocked-count"
            >
              {unlockedCount}/{packs.length || 3} desbloqueados
            </span>
          </div>
        </header>

        {loading ? (
          <div
            className="q-glass border border-white/10 rounded-3xl p-10 text-center text-slate-400"
            data-testid="market-loading"
          >
            <Loader2 className="mx-auto animate-spin q-accent-text mb-3" size={28} />
            Cargando el aparador…
          </div>
        ) : packs.length === 0 ? (
          <div
            className="q-glass border border-white/10 rounded-3xl p-10 text-center text-slate-400"
            data-testid="market-empty"
          >
            Aún no hay packs disponibles. Vuelve pronto.
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {packs.map((p) => (
              <PackCard
                key={p.id}
                pack={p}
                busy={purchasingId === p.id}
                onPurchase={onPurchase}
              />
            ))}
          </div>
        )}

        <p
          className="mt-8 text-center text-[11px] uppercase tracking-widest font-mono text-slate-500"
          data-testid="market-price-note"
        >
          Precio uniforme: {STICKER_PACK_PRICE} ⚡ NexaTokens · sin renovaciones · sin caducidad
        </p>
      </div>

      <InsufficientBalanceModal
        open={!!insufficient}
        missing={insufficient?.missing || STICKER_PACK_PRICE}
        ondatag={user?.ondatag}
        onClose={() => setInsufficient(null)}
      />

      {/* Holographic shine — scoped to this page */}
      <style>{`
        .pack-card {
          transition: transform 220ms ease, box-shadow 220ms ease;
        }
        .pack-card:hover {
          transform: translateY(-3px);
        }
        .pack-card:hover .pack-shine {
          transform: translateX(150%) skewX(-18deg);
        }
        .pack-card .pack-shine {
          position: absolute;
          inset: 0;
          background: linear-gradient(
            110deg,
            transparent 40%,
            var(--pack-accent, #06B6D4) 50%,
            transparent 60%
          );
          opacity: 0.28;
          mix-blend-mode: screen;
          transform: translateX(-150%) skewX(-18deg);
          transition: transform 900ms cubic-bezier(0.16, 0.84, 0.44, 1);
          pointer-events: none;
        }
        .pack-cover {
          transition: transform 400ms ease, filter 400ms ease;
        }
        .pack-card:hover .pack-cover {
          transform: scale(1.04);
          filter: saturate(1.2) contrast(1.05);
        }
        .pack-sticker-tile {
          transition: transform 220ms ease, box-shadow 220ms ease;
        }
        .pack-sticker-tile:hover {
          transform: translateY(-2px) scale(1.02);
          box-shadow: 0 0 14px rgba(0, 240, 255, 0.35);
        }
      `}</style>
    </main>
  );
}
